import type { DocumentTableRow } from 'features/knowledgeDocumentViewer'
import {
  buildDocumentResultCardItems,
  type DocumentResultCardItem,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentResultCardItems'
import type {
  DrawingRevisionGroup,
  Highlight,
  PageClassification,
  SearchResultDoc,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type {
  DocChatResponseEvent,
  DocChatResponsePartialEvent,
} from 'features/knowledgeDocumentViewer/types'
import {
  unscopePageIdFromPipelineConfig,
  unscopeSearchDocumentIdForHydration,
} from 'features/knowledgeDocumentViewer/utils/pipelineConfigScopedIds'
import { retrievedWorkOrderRows } from 'features/knowledgeDocumentViewer/workOrders/workOrder'

type DocChatEvent =
  | DocChatResponseEvent
  | DocChatResponsePartialEvent
  | Pick<DocChatResponseEvent, 'results' | 'response_segments' | 'cmms'>

function indexDocumentIfAbsent(
  documentsById: Map<string, DocumentTableRow>,
  document: DocumentTableRow
) {
  if (!documentsById.has(document.id)) {
    documentsById.set(document.id, document)
  }
  if (document.bqDocumentId && !documentsById.has(document.bqDocumentId)) {
    documentsById.set(document.bqDocumentId, document)
  }
}

function indexDocumentsIfAbsent(
  documentsById: Map<string, DocumentTableRow>,
  documents: Iterable<DocumentTableRow>
) {
  for (const document of documents) {
    indexDocumentIfAbsent(documentsById, document)
  }
}

function remapEvidenceMapToDocumentIds<T>(
  valuesBySearchId: Map<string, T>,
  documents: DocumentTableRow[]
): Map<string, T> {
  const remapped = new Map<string, T>()
  for (const document of documents) {
    const value =
      valuesBySearchId.get(document.id) ??
      (document.bqDocumentId
        ? valuesBySearchId.get(document.bqDocumentId)
        : undefined)
    if (value) remapped.set(document.id, value)
  }
  return remapped
}

function overlayPresentMapEntries<T>(
  fallback: Map<string, T>,
  overlay: Map<string, T>,
  isPresent: (value: T) => boolean
): Map<string, T> {
  const merged = new Map(fallback)
  for (const [key, value] of overlay) {
    if (isPresent(value)) merged.set(key, value)
  }
  return merged
}

function unscopedHighlights(
  highlights: Highlight[],
  selectedPipelineConfigId?: string
): Highlight[] {
  return highlights.map((highlight) => ({
    ...highlight,
    page_id:
      typeof highlight.page_id === 'string'
        ? unscopePageIdFromPipelineConfig(
            highlight.page_id,
            selectedPipelineConfigId
          )
        : highlight.page_id,
  }))
}

function unscopedRevisionGroups(
  groups: DrawingRevisionGroup[],
  selectedPipelineConfigId?: string
): DrawingRevisionGroup[] {
  return groups.map((group) => ({
    ...group,
    options: group.options.map((option) => ({
      ...option,
      document_id: unscopeSearchDocumentIdForHydration(
        option.document_id,
        selectedPipelineConfigId
      ),
    })),
  }))
}

export function getAgenticInlineEvidenceMaps({
  response,
  selectedPipelineConfigId,
}: {
  response: DocChatEvent
  selectedPipelineConfigId?: string
}): {
  drawingRevisionGroupsMap: Map<string, DrawingRevisionGroup[]>
  highlightsMap: Map<string, Highlight[]>
  pageClassificationsMap: Map<string, PageClassification[]>
} {
  const drawingRevisionGroupsMap = new Map<string, DrawingRevisionGroup[]>()
  const highlightsMap = new Map<string, Highlight[]>()
  const pageClassificationsMap = new Map<string, PageClassification[]>()

  for (const result of response.results ?? []) {
    setEvidenceMapsFromResult({
      drawingRevisionGroupsMap,
      highlightsMap,
      pageClassificationsMap,
      result,
      selectedPipelineConfigId,
    })
  }

  return {
    drawingRevisionGroupsMap,
    highlightsMap,
    pageClassificationsMap,
  }
}

function setEvidenceMapsFromResult({
  drawingRevisionGroupsMap,
  highlightsMap,
  pageClassificationsMap,
  result,
  selectedPipelineConfigId,
}: {
  drawingRevisionGroupsMap: Map<string, DrawingRevisionGroup[]>
  highlightsMap: Map<string, Highlight[]>
  pageClassificationsMap: Map<string, PageClassification[]>
  result: SearchResultDoc
  selectedPipelineConfigId?: string
}) {
  const searchId = unscopeSearchDocumentIdForHydration(
    result.document_id,
    selectedPipelineConfigId
  )
  if (result.highlights && result.highlights.length > 0) {
    highlightsMap.set(
      searchId,
      unscopedHighlights(result.highlights, selectedPipelineConfigId)
    )
  }
  if (result.page_classifications && result.page_classifications.length > 0) {
    pageClassificationsMap.set(searchId, result.page_classifications)
  }
  if (
    result.drawing_revision_groups &&
    result.drawing_revision_groups.length > 0
  ) {
    drawingRevisionGroupsMap.set(
      searchId,
      unscopedRevisionGroups(
        result.drawing_revision_groups,
        selectedPipelineConfigId
      )
    )
  }
}

export function getOrderedAgenticDocumentResults({
  documentResults,
  documentRowsById,
  response,
  selectedPipelineConfigId,
}: {
  documentResults: DocumentTableRow[]
  documentRowsById: Map<string, DocumentTableRow>
  response: DocChatEvent
  selectedPipelineConfigId?: string
}): DocumentTableRow[] {
  const documentsById = new Map<string, DocumentTableRow>()
  indexDocumentsIfAbsent(documentsById, documentRowsById.values())
  indexDocumentsIfAbsent(documentsById, documentResults)

  const seen = new Set<string>()
  const documents = (response.results ?? []).flatMap((result) => {
    const searchId = unscopeSearchDocumentIdForHydration(
      result.document_id,
      selectedPipelineConfigId
    )
    const document = documentsById.get(searchId)
    if (!document || seen.has(document.id)) return []
    seen.add(document.id)
    return [document]
  })
  const workOrders = retrievedWorkOrderRows(response)
  return [...documents, ...workOrders.filter((row) => !seen.has(row.id))]
}

export function getAgenticInlineDocumentCardItems({
  documentResults,
  documentRowsById,
  drawingRevisionGroupsMap,
  response,
  selectedPipelineConfigId,
}: {
  documentResults: DocumentTableRow[]
  documentRowsById: Map<string, DocumentTableRow>
  drawingRevisionGroupsMap?: Map<string, DrawingRevisionGroup[]>
  response: DocChatEvent
  selectedPipelineConfigId?: string
}): DocumentResultCardItem[] {
  const documents = getOrderedAgenticDocumentResults({
    documentResults,
    documentRowsById,
    response,
    selectedPipelineConfigId,
  })
  const snapshotMaps = getAgenticInlineEvidenceMaps({
    response,
    selectedPipelineConfigId,
  })
  const snapshotRevisionGroups = remapEvidenceMapToDocumentIds(
    snapshotMaps.drawingRevisionGroupsMap,
    documents
  )
  const fallbackRevisionGroups = drawingRevisionGroupsMap
    ? remapEvidenceMapToDocumentIds(drawingRevisionGroupsMap, documents)
    : new Map<string, DrawingRevisionGroup[]>()
  return buildDocumentResultCardItems({
    documents,
    documentRowsById,
    drawingRevisionGroupsMap: overlayPresentMapEntries(
      fallbackRevisionGroups,
      snapshotRevisionGroups,
      (groups) => groups.length > 0
    ),
    enablePageCardSurfacing: true,
    highlightsMap: remapEvidenceMapToDocumentIds(
      snapshotMaps.highlightsMap,
      documents
    ),
    pageClassificationsMap: remapEvidenceMapToDocumentIds(
      snapshotMaps.pageClassificationsMap,
      documents
    ),
  })
}
